import { TranslateService } from '@ngx-translate/core';

/**
 * Color palettes for each indicator - used for multi-year comparison
 * Each array contains mixed colors (bright + dark) for maximum distinction
 * Colors are arranged to alternate between vibrant and dark for best differentiation
 */
export const INDICATOR_COLOR_PALETTES: { [key: string]: string[] } = {
  // Temperature - Mixed vibrant and dark colors
  temperature: [
    '#ef4444', // Bright Red
    '#00d4ff', // Electric Cyan
    '#8b4513', // Brown
    '#ffd700', // Gold
    '#000000', // Black
    '#ff00ff', // Magenta
    '#333333', // Dark Gray
    '#1a1a1a', // Very Dark Gray
  ],
  // Precipitation - Mixed vibrant and dark colors
  precipitation: [
    '#0066ff', // Deep Blue
    '#ff8c00', // Dark Orange
    '#ff1493', // Deep Pink
    '#0d0d0d', // Nearly Black
    '#32cd32', // Lime Green
    '#666666', // Medium Gray
    '#654321', // Dark Brown
    '#262626', // Dark Gray-Black
  ],
  // Humidity - Mixed vibrant and dark colors
  humidity: [
    '#00ff00', // Bright Green
    '#0000ff', // Blue
    '#555555', // Gray
    '#ff6600', // Orange
    '#1f1f1f', // Almost Black
    '#0099ff', // Sky Blue
    '#ff0066', // Hot Pink
    '#333333', // Dark Gray
  ],
  // Wind Speed - Mixed vibrant and dark colors
  windSpeed: [
    '#8b00ff', // Electric Violet

    '#8b6914', // Brown-Gold
    '#00ffff', // Cyan
    '#0d0d0d', // Nearly Black
    '#ff0000', // Red
    '#444444', // Dark Gray
    '#262626', // Dark Gray-Black
  ],
  // Solar Radiation - Mixed vibrant and dark colors
  solarRadiation: [
    '#ffaa00', // Orange-Gold
    '#1a1a1a', // Very Dark Gray
    '#00cc99', // Teal
    '#654321', // Dark Brown
    '#6600ff', // Purple-Blue
    '#555555', // Gray
    '#ffcc00', // Yellow-Gold
    '#0d0d0d', // Nearly Black
  ],
};

/**
 * Indicator configuration interface
 */
export interface IndicatorConfig {
  key: string;
  label: string;
  color: string;
  unit: string;
  aliases: string[];
}

/**
 * Solar radiation conversion coefficient
 * Converts Watts/m² to kcal/cm²
 */
export const SOLAR_RADIATION_CONVERSION_COEFFICIENT = 42.4;

/**
 * Manager for weather indicator configuration
 * Centralizes indicator colors, units, and value extraction logic
 */
export class WeatherIndicatorManager {
  private indicators: Map<string, IndicatorConfig> = new Map();

  constructor(private translateService: TranslateService) {
    this.initializeIndicators();
  }

  /**
   * Initialize indicator configurations
   */
  private initializeIndicators(): void {
    const configs: IndicatorConfig[] = [
      {
        key: 'temperature',
        label: 'Temperature',
        color: '#ef4444',
        unit: '°C',
        aliases: ['temp', 'temperature']
      },
      {
        key: 'precipitation',
        label: 'Fallout',
        color: '#3b82f6',
        unit: 'gis.unit.mm',
        aliases: ['precip', 'fall', 'fallout', 'rain', 'precipitation']
      },
      {
        key: 'humidity',
        label: 'Humidity',
        color: '#10b981',
        unit: '%',
        aliases: ['humid', 'humidity']
      },
      {
        key: 'windSpeed',
        label: 'WIND_SPEED',
        color: '#8b5cf6',
        unit: 'gis.unit.ms',
        aliases: ['wind', 'windspeed']
      },
      {
        key: 'solarRadiation',
        label: 'SolarRadiationInfluence',
        color: '#f59e0b',
        unit: 'gis.unit.kcal',
        aliases: ['solar', 'radiation', 'solarradiation']
      }
    ];

    configs.forEach(config => {
      this.indicators.set(config.key, config);
    });
  }

  /**
   * Get indicator configuration by key or alias
   */
  getIndicatorConfig(indicator: string): IndicatorConfig | undefined {
    const ind = (indicator || '').toLowerCase();

    // Try exact key match first
    if (this.indicators.has(indicator)) {
      return this.indicators.get(indicator);
    }

    // Try alias match
    for (const config of this.indicators.values()) {
      if (config.aliases.some(alias => ind.includes(alias))) {
        return config;
      }
    }

    return undefined;
  }

  /**
   * Get color for indicator
   */
  getColor(indicator: string): string {
    const config = this.getIndicatorConfig(indicator);
    return config?.color || '#6b7280';
  }

  /**
   * Get color from palette by indicator and year index
   * Used for multi-year comparison to differentiate years of the same indicator
   */
  getColorByIndicatorAndYear(indicator: string, yearIndex: number): string {
    const config = this.getIndicatorConfig(indicator);
    if (!config) return '#6b7280';

    const palette = INDICATOR_COLOR_PALETTES[config.key];
    if (!palette || palette.length === 0) return '#6b7280';

    // Cycle through palette if yearIndex exceeds palette size
    return palette[yearIndex % palette.length];
  }

  /**
   * Get unit for indicator (translated if needed)
   */
  getUnit(indicator: string): string {
    const config = this.getIndicatorConfig(indicator);
    if (!config) return '';

    // Check if unit needs translation
    if (config.unit.includes('.')) {
      return this.translateService.instant(config.unit);
    }
    return config.unit;
  }

  /**
   * Get translated label for indicator
   */
  getLabel(indicator: string): string {
    const config = this.getIndicatorConfig(indicator);
    if (!config) return indicator;
    return this.translateService.instant(config.label);
  }

  /**
   * Normalize solar radiation value based on data source
   * ERA5: values are already in kcal/cm², no conversion needed
   * UGM/IBM: values are in Watts/m², convert to kcal/cm²
   */
  normalizeSolarRadiation(value: number | null | undefined, source: string): number {
    if (value === null || value === undefined || value <= 0) {
      return 0;
    }

    // ERA5 values are already in kcal/cm²
    if (source === 'era5') {
      return Math.round(value);
    }

    // UGM and IBM values are in Watts/m², convert to kcal/cm²
    if (source === 'ugm' || source === 'ibm') {
      return Math.round(value / SOLAR_RADIATION_CONVERSION_COEFFICIENT);
    }

    return Math.round(value);
  }

  /**
   * Extract indicator value from weather data
   */
  extractValue(data: any, indicator: string, dataSource: string = 'era5'): number {
    const ind = (indicator || '').toLowerCase();

    const toNumber = (v: any, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    // Temperature
    if (ind.includes('temp') || ind.includes('temperature')) {
      return toNumber(
        data.temperatureAverage ??
        data.temperature ??
        data.temperatureMin ??
        data.temperatureMax ??
        data.temperatureValue ?? 0
      );
    }

    // Precipitation / fallout
    if (ind.includes('precip') || ind.includes('fall') || ind.includes('fallout') || ind.includes('rain')) {
      return toNumber(data.fallout ?? data.precipitation ?? data.precipitationAmount ?? 0);
    }

    // Humidity
    if (ind.includes('humid')) {
      return toNumber(data.humidity ?? 0);
    }

    // Wind speed
    if (ind.includes('wind')) {
      return toNumber(data.windSpeed ?? data.wind_speed ?? 0);
    }

    // Solar radiation
    if (ind.includes('solar') || ind.includes('radiation')) {
      const rawValue = toNumber(data.solarRadiationInfluence ?? data.solarRadiation ?? 0);
      // Determine source for normalization
      const source = data.solarRadiationInfluence !== null && data.solarRadiationInfluence !== undefined
        ? 'era5'
        : dataSource;
      return this.normalizeSolarRadiation(rawValue, source);
    }

    return 0;
  }

  /**
   * Get all indicator options for dropdown
   */
  getIndicatorOptions(): { label: string; value: string }[] {
    return [
      { label: 'Temperature', value: 'temperature' },
      { label: 'Fallout', value: 'precipitation' },
      { label: 'Humidity', value: 'humidity' },
      { label: 'WIND_SPEED', value: 'windSpeed' },
      { label: 'SolarRadiationInfluence', value: 'solarRadiation' }
    ];
  }

  /**
   * Get translated month names
   */
  getTranslatedMonthNames(): string[] {
    return [
      this.translateService.instant('MONTH_JAN'),
      this.translateService.instant('MONTH_FEB'),
      this.translateService.instant('MONTH_MAR'),
      this.translateService.instant('MONTH_APR'),
      this.translateService.instant('MONTH_MAY'),
      this.translateService.instant('MONTH_JUN'),
      this.translateService.instant('MONTH_JUL'),
      this.translateService.instant('MONTH_AUG'),
      this.translateService.instant('MONTH_SEP'),
      this.translateService.instant('MONTH_OCT'),
      this.translateService.instant('MONTH_NOV'),
      this.translateService.instant('MONTH_DEC')
    ];
  }
}
